import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Modal, Form, InputNumber, Input, Select, message, Divider, DatePicker, Tag, Space } from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import { rm2FeeApi, vehicleCapacityTonsApi, locationApi } from '../services/api';
import { RM2Fee, VehicleCapacityTons, ProvinceWithDistricts, DistrictDto, RM2FeeHistory } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { getRM2FeeColumns, getRM2FeeHistoryColumns } from '../components/rm2Fee/RM2FeeTableColumns';

const { Option } = Select;
const { TextArea } = Input;

const RM2FeePage: React.FC = () => {
  const { t } = useLanguage();
  const [rm2Fees, setRM2Fees] = useState<RM2Fee[]>([]);
  const [capacityTons, setCapacityTons] = useState<VehicleCapacityTons[]>([]);
  const [provinces, setProvinces] = useState<ProvinceWithDistricts[]>([]);
  const [districts, setDistricts] = useState<DistrictDto[]>([]);
  const [selectedProvinceId, setSelectedProvinceId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [editingRM2Fee, setEditingRM2Fee] = useState<RM2Fee | null>(null);
  const [history, setHistory] = useState<RM2FeeHistory[]>([]);
  const [selectedAvailableDistrictIds, setSelectedAvailableDistrictIds] = useState<number[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchRM2Fees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rm2FeeApi.getAll();
      setRM2Fees(response.data);
    } catch (error) {
      console.error('Error fetching RM2 fees:', error);
      message.error(t('failed.to.fetch.rm2.fees'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchCapacityTons = async () => {
    try {
      const response = await vehicleCapacityTonsApi.getAll();
      setCapacityTons(response.data);
    } catch (error) {
      console.error('Error fetching capacity tons:', error);
    }
  };

  const fetchProvincesWithDistricts = async () => {
    try {
      const response = await locationApi.getProvincesWithDistricts();
      setProvinces(response.data);
    } catch (error) {
      console.error('Error fetching provinces with districts:', error);
    }
  };

  const fetchDistricts = useCallback((provinceId?: number) => {
    if (!provinceId) {
      setDistricts([]);
      return;
    }
    const province = provinces.find(p => p.id === provinceId);
    setDistricts(province ? province.districts : []);
  }, [provinces]);

  useEffect(() => {
    fetchRM2Fees();
    fetchCapacityTons();
    fetchProvincesWithDistricts();
  }, [fetchRM2Fees]);

  useEffect(() => {
    fetchDistricts(selectedProvinceId);
  }, [fetchDistricts, selectedProvinceId]);

  const showAddModal = () => {
    setEditingRM2Fee(null);
    form.resetFields();
    form.setFieldsValue({
      affectedDate: dayjs()
    });
    setModalVisible(true);
  };

  const showEditModal = async (rm2Fee: RM2Fee) => {
    setEditingRM2Fee(rm2Fee);

    if (rm2Fee.districts.length > 0) {
      const firstDistrictId = rm2Fee.districts[0].districtId ?? rm2Fee.districts[0].id;
      // Find provinceId by searching all provinces for the district
      let provinceId: number | undefined;
      for (const province of provinces) {
        if (province.districts && province.districts.some((d: any) => d.id === firstDistrictId)) {
          provinceId = province.id;
          break;
        }
      }
      if (provinceId) {
        setSelectedProvinceId(provinceId);
        setTimeout(() => {
          form.setFieldsValue({
            capacityTonsId: rm2Fee.capacityTonsId,
            fee: rm2Fee.fee,
            notes: rm2Fee.notes,
            affectedDate: dayjs(rm2Fee.affectedDate),
            districtIds: rm2Fee.districts.map(d => d.districtId ?? d.id)
          });
          setModalVisible(true);
        }, 0);
        return;
      }
    }

    // If no districts, or failed to find province, set form and open modal immediately
    form.setFieldsValue({
      capacityTonsId: rm2Fee.capacityTonsId,
      fee: rm2Fee.fee,
      notes: rm2Fee.notes,
      affectedDate: dayjs(rm2Fee.affectedDate),
      districtIds: rm2Fee.districts.map(d => d.districtId ?? d.id)
    });
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingRM2Fee(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        capacityTonsId: values.capacityTonsId || null,
        fee: values.fee,
        notes: values.notes,
        affectedDate: values.affectedDate ? values.affectedDate.format('YYYY-MM-DD') : undefined,
        reason: values.reason,
        districtIds: values.districtIds || []
      };

      if (editingRM2Fee) {
        await rm2FeeApi.update(editingRM2Fee.id, data);
        message.success(t('rm2.fee.updated.successfully'));
      } else {
        await rm2FeeApi.create(data);
        message.success(t('rm2.fee.created.successfully'));
      }

      setModalVisible(false);
      form.resetFields();
      fetchRM2Fees();
    } catch (error) {
      console.error('Error saving RM2 fee:', error);
      message.error(editingRM2Fee ? t('error.updating.rm2.fee') : t('error.creating.rm2.fee'));
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await rm2FeeApi.delete(id);
      message.success(t('rm2.fee.deleted.successfully'));
      fetchRM2Fees();
    } catch (error: any) {
      console.error('Error deleting RM2 fee:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        message.error(t('error.deleting.rm2.fee') + ': ' + (error?.response?.data || 'Unauthorized'));
      } else {
        message.error(t('error.deleting.rm2.fee'));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const showHistory = async (rm2Fee: RM2Fee) => {
    try {
      const response = await rm2FeeApi.getHistory(rm2Fee.id);
      setHistory(response.data);
      setHistoryModalVisible(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      message.error(t('failed.to.fetch.history'));
    }
  };

  // Add a district to the selected list
  const addDistrictToList = (districtId: number) => {
    const currentDistrictIds = form.getFieldValue('districtIds') || [];
    if (!currentDistrictIds.includes(districtId)) {
      const newDistrictIds = [...currentDistrictIds, districtId];
      form.setFieldsValue({ districtIds: newDistrictIds });
    }
  };

  // Remove a district from the selected list
  const removeDistrictFromList = (districtId: number) => {
    const currentDistrictIds = form.getFieldValue('districtIds') || [];
    const newDistrictIds = currentDistrictIds.filter((id: number) => id !== districtId);
    form.setFieldsValue({ districtIds: newDistrictIds });
  };

  // Helper to get selected districts (left list)
  const selectedDistrictIds: number[] = form.getFieldValue('districtIds') || [];
  const selectedDistricts: DistrictDto[] = selectedDistrictIds
    .map((id: number) => {
      // Try to find in all districts of the selected province, fallback to any loaded districts
      return (
        districts.find(d => d.id === id) ||
        provinces.flatMap(p => (p as any).districts || []).find((d: DistrictDto) => d.id === id)
      );
    })
    .filter(Boolean);

  // Right list: available districts from selected province, not already selected
  const availableDistricts = districts.filter(d => !selectedDistrictIds.includes(d.id));

  // Add selected available district to selected list
  const handleAddDistrict = () => {
    if (selectedAvailableDistrictIds.length > 0) {
      selectedAvailableDistrictIds.forEach(id => addDistrictToList(id));
      setSelectedAvailableDistrictIds([]);
    }
  };

  // Toggle selection for available districts
  const toggleAvailableDistrictSelection = (districtId: number) => {
    setSelectedAvailableDistrictIds(prev =>
      prev.includes(districtId)
        ? prev.filter(id => id !== districtId)
        : [...prev, districtId]
    );
  };

  const columns = getRM2FeeColumns({
    t,
    onEdit: showEditModal,
    onShowHistory: showHistory,
    onDelete: handleDelete,
    deletingId
  });

  const historyColumns = getRM2FeeHistoryColumns(t);

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={t('rm2.fee.management')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
            {t('add.rm2.fee')}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={rm2Fees}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingRM2Fee ? t('edit.rm2.fee') : t('add.rm2.fee')}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={t('vehicle.capacity.optional')}
            name="capacityTonsId"
            help={t('leave.empty.for.all.capacities')}
          >
            <Select placeholder={t('select.capacity')} allowClear>
              {capacityTons.map(capacity => (
                <Option key={capacity.id} value={capacity.id}>
                  {capacity.capacity} {t('tons')}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('fee.amount')}
            name="fee"
            rules={[
              { required: true, message: t('please.enter.fee.amount') },
              { type: 'number', min: 0, message: t('fee.must.be.greater.than.zero') }
            ]}
          >
            <InputNumber
              min={0}
              step={1000}
              style={{ width: '100%' }}
              placeholder={t('enter.fee.amount')}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as unknown as 0}
            />
          </Form.Item>

          <Divider>{t('district.management')}</Divider>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            {/* Left: Selected Districts */}
            <div style={{ flex: 1, minWidth: 200, border: '1px solid #eee', borderRadius: 4, padding: 8, marginRight: 16, minHeight: 180 }}>
              <strong>{t('selected.districts')}</strong>
              <div style={{ marginTop: 8 }}>
                {selectedDistricts.length === 0 && (
                  <span style={{ color: '#999' }}>{t('no.districts.selected')}</span>
                )}
                {selectedDistricts.map(district => {
                  const province = provinces.find(p => p.districts.some(d2 => d2.id === district.id));
                  return (
                    <div key={district.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                      <Tag color="blue" style={{ flex: 1 }}>{district.name}, {province?.name}</Tag>
                      <Button size="small" danger onClick={() => removeDistrictFromList(district.id)} style={{ marginLeft: 8 }}>{t('remove')}</Button>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Middle: Arrow Button */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 16px' }}>
              <Button
                type="primary"
                icon={<span style={{ fontWeight: 'bold' }}>{'←'}</span>}
                disabled={selectedAvailableDistrictIds.length === 0}
                onClick={handleAddDistrict}
                style={{ marginBottom: 8 }}
              >
              </Button>
            </div>
            {/* Right: Available Districts */}
            <div style={{ flex: 1, minWidth: 200, border: '1px solid #eee', borderRadius: 4, padding: 8, minHeight: 180 }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ marginRight: 8 }}>{t('province.filter')}:</span>
                <Select
                  placeholder={t('select.province.to.filter.districts')}
                  style={{ width: 180 }}
                  allowClear
                  value={selectedProvinceId}
                  onChange={setSelectedProvinceId}
                >
                  {provinces.map(province => (
                    <Option key={province.id} value={province.id}>
                      {province.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <strong>{t('available.districts')}</strong>
              <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto' }}>
                {availableDistricts.length === 0 && (
                  <span style={{ color: '#999' }}>{t('no.districts.available')}</span>
                )}
                {availableDistricts.map(district => (
                  <div
                    key={district.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: selectedAvailableDistrictIds.includes(district.id) ? '#e6f7ff' : undefined,
                      cursor: 'pointer',
                      borderRadius: 4,
                      padding: '2px 6px',
                      marginBottom: 2
                    }}
                    onClick={() => toggleAvailableDistrictSelection(district.id)}
                  >
                    <span style={{ flex: 1 }}>{district.name}</span>
                    {selectedAvailableDistrictIds.includes(district.id) && (
                      <span style={{ color: '#1890ff', marginLeft: 8 }}>&#10003;</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Form.Item name="districtIds" hidden>
            <Input />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <strong>{t('selected.districts')}:</strong>
            <div style={{ marginTop: 8 }}>
              {(form.getFieldValue('districtIds') || []).map((districtId: number) => {
                const district = districts.find(d => d.id === districtId);
                const province = district ? provinces.find(p => p.districts.some(d2 => d2.id === district.id)) : undefined;
                return district ? (
                  <Tag 
                    key={districtId} 
                    closable 
                    color="blue"
                    onClose={() => removeDistrictFromList(districtId)}
                  >
                    {district.name}, {province?.name}
                  </Tag>
                ) : null;
              })}
              {(!form.getFieldValue('districtIds') || form.getFieldValue('districtIds').length === 0) && (
                <span style={{ color: '#999' }}>{t('no.districts.selected')}</span>
              )}
            </div>
          </div>

          <Form.Item label={t('notes')} name="notes">
            <TextArea rows={3} placeholder={t('optional.notes.about.rm2.fee')} />
          </Form.Item>

          <Form.Item
            label={t('affected.date')}
            name="affectedDate"
            rules={[{ required: true, message: t('please.select') + ' ' + t('affected.date') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('please.select') + ' ' + t('affected.date')}
            />
          </Form.Item>

          <Form.Item
            label={t('reason.for.change')}
            name="reason"
            rules={editingRM2Fee ? [{ required: true, message: t('please.provide.reason.for.change') }] : []}
          >
            <TextArea rows={2} placeholder={t('reason.for.creating.updating.rm2.fee')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRM2Fee ? t('update') : t('create')}
              </Button>
              <Button onClick={handleCancel}>
                {t('cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('rm2.fee.history')}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={historyColumns}
          dataSource={history}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default RM2FeePage;
